import React, { useEffect, useMemo, useState } from "react";
import ReusableTable from "../../components/Employee/Table";
import "./LeavesListLayout.css";
import { Navigate, useNavigate } from "react-router-dom";
import { useGetAllLeaveRequestsPaginated } from "../../hooks/useLeave";
import { formatDateOnly, getMediaBase64 } from "../../util/common";

export default function LeavesListLayout() {
  const [pageCount, setPageCount] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
  });

  const [filter, setFilter] = useState({
    ApprovalStatus: null,
    OrderBy: "desc",
  });

  const leaveRequests = useGetAllLeaveRequestsPaginated();
  const [leaveRecords, setLeaveRecords] = useState([]);

  const fetchLeaveRecords = () => {
    leaveRequests.mutate(
      {
        page: pagination.page,
        pageSize: pagination.pageSize,
        filters: filter,
      },
      {
        onSuccess: async (result) => {
          if (!result.success) return;

          const records = await Promise.all(
            result.data.data.map(async (record) => {
              return {
                StartDate: formatDateOnly(record.StartDate),
                EndDate: formatDateOnly(record.EndDate),
                StartTime: record.StartTime,
                EndTime: record.EndTime,
                DurationDays: record.DurationDays,
                LeaveType: record.LeaveType?.Name,
                Reason: record.Reason,
                Media: await getMediaBase64(record?.Media?.MediaUrl),
                Approver: record.Approver?.FullName,
                ApprovedAt: formatDateOnly(record.ApprovedAt),
                ApprovalStatus: record.ApprovalStatus,
              };
            })
          );
          setLeaveRecords(records);
          setPageCount(result.data.pagination.totalPages);
        },
      }
    );
  };

  useEffect(() => {
    fetchLeaveRecords();
  }, [pagination, filter]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "StartDate",
        header: "Date From",
      },
      {
        accessorKey: "EndDate",
        header: "Date To",
      },
      {
        accessorKey: "StartTime",
        header: "Time From",
      },
      {
        accessorKey: "EndTime",
        header: "Time To",
      },
      {
        accessorKey: "DurationDays",
        header: "Duration Days",
      },
      {
        accessorKey: "LeaveType",
        header: "Leave Type",
      },
      {
        accessorKey: "Reason",
        header: "Reason",
      },
      {
        accessorKey: "Media",
        header: "Media",
        cell: ({ getValue }) => {
          const url = getValue();
          if (!url) return "No Image";
          return (
            <img
              src={url}
              alt="Media"
              style={{
                width: "60px",
                height: "60px",
                objectFit: "cover",
                borderRadius: "6px",
              }}
            />
          );
        },
      },
      {
        accessorKey: "Approver",
        header: "Approver Name",
      },
      {
        accessorKey: "ApprovedAt",
        header: "Approved At",
      },
      {
        accessorKey: "ApprovalStatus",
        header: "Status",
        cell: ({ row }) => {
          const status = row.original.ApprovalStatus;
          return (
            <span className={`status-badge status-${status.toLowerCase()}`}>
              {status}
            </span>
          );
        },
      },
    ],
    []
  );

  const handleOnPageChange = (pageIndex, pageSize) => {
    setPagination({
      page: pageIndex + 1,
      pageSize,
    });
  };

  const navigate = useNavigate();

  return (
    <div className="overtime-list-container">
      <div className="header-row">
        <h3>Leaves Records</h3>

        <div className="filters-right">
          <button className="overtime-btn" onClick={() => navigate("/Leaves")}>
            + Leave
          </button>

          <select
            value={filter.ApprovalStatus}
            className="status-filter"
            onChange={(e) => {
              setFilter({
                ApprovalStatus: e.target.value,
                OrderBy: filter.OrderBy,
              });
            }}
          >
            <option value="">All Status</option>
            <option value="Approved">Approved</option>
            <option value="Pending">Pending</option>
            <option value="Cancelled">Cancelled</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
      </div>

      <ReusableTable
        data={leaveRecords}
        columns={columns}
        mode="server"
        initialPageSize={pagination.pageSize}
        onPageChange={handleOnPageChange}
        pageCount={pageCount}
        page={pagination.page - 1}
        pageSize={pagination.pageSize}
      />
    </div>
  );
}
